import React, { useEffect, useRef, useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Explore as ExploreIcon,
  Assignment as TestCasesIcon,
  Build as AutomationIcon,
  Chat as ChatIcon,
  Assessment as AssessmentIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  AccountCircle as UserIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  VerifiedUser as EligibilityIcon,
  Source as RepositorySourceIcon,
} from '@mui/icons-material';
import ProfileDialog from './ProfileDialog';
import ThemeToggle from './ThemeToggle';
import SidebarProjectSwitcher from './ProjectSwitcher/SidebarProjectSwitcher';
import useAvailableProjects from './ProjectSwitcher/useAvailableProjects';
import { reconcileSelectedProject } from './ProjectSwitcher/validateSelectedProject';
import { isSameProject } from './ProjectSwitcher/projectUtils';
import { useTheme as useCustomTheme } from '../theme/ThemeProvider';
import { SV } from '../automation/stratovaiTestIds';

const drawerWidth = 280;

const ModernNavigation = ({
  authenticatedUser,
  selectedProject,
  activeTab,
  onTabChange,
  onLogout,
  onNavigateToUserManager,
  onNavigateToSettings,
  onOpenChangePassword,
  onProjectSwitch,
  onProjectReconcile,
  apiClient,
  projectSummary,
}) => {
  const { isDarkMode } = useCustomTheme();
  const { projects, loading: projectsLoading, error: projectsError } = useAvailableProjects(
    apiClient,
    authenticatedUser
  );
  const reconcileAttemptedRef = useRef(false);

  useEffect(() => {
    if (projectsLoading || reconcileAttemptedRef.current) return;
    if (!authenticatedUser || !projects.length) return;

    const { project, nextStep } = reconcileSelectedProject(projects, selectedProject);
    const isValid =
      nextStep === 'continue' &&
      selectedProject &&
      project &&
      isSameProject(project, selectedProject);

    if (isValid) {
      reconcileAttemptedRef.current = true;
      return;
    }

    reconcileAttemptedRef.current = true;
    if (onProjectReconcile) {
      onProjectReconcile(project, nextStep);
    }
  }, [
    authenticatedUser,
    onProjectReconcile,
    projects,
    projectsLoading,
    selectedProject,
  ]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const isProjectAdmin = authenticatedUser?.role === 'project_admin' || authenticatedUser?.role === 'super_admin';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleProfileOpen = () => {
    setProfileOpen(true);
    handleUserMenuClose();
  };

  const handleProfileClose = () => {
    setProfileOpen(false);
  };

  // Active navigation items
  const activeNavigationItems = [
    {
      id: 'overview',
      label: 'Overview',
      icon: <DashboardIcon />,
      description: 'Project dashboards & insights',
    },
    {
      id: 'quality',
      label: 'Quality',
      icon: <BarChartIcon />,
      description: 'Defect analytics and metrics',
    },
    {
      id: 'test-cases',
      label: 'Test Cases',
      icon: <TestCasesIcon />,
      description: 'Manage test cases and scenarios',
    },
    {
      id: 'chat',
      label: 'AI Chat',
      icon: <ChatIcon />,
      description: 'Interactive AI assistance',
    },
    {
      id: 'product-knowledge',
      label: 'Product Knowledge',
      icon: <AssessmentIcon />,
      description: 'Ingest, organize & curate Product Knowledge',
    },
    ...(isProjectAdmin ? [{
      id: 'safe-summary-eligibility',
      label: 'Safe Summary Eligibility',
      icon: <EligibilityIcon />,
      description: 'Read-only safe summary eligibility admin view',
    }] : []),
    ...(isProjectAdmin ? [{
      id: 'repository-source-config',
      label: 'Repository Sources',
      icon: <RepositorySourceIcon />,
      description: 'Read-only repository source configuration admin view',
    }] : []),
    {
      id: 'health-insights',
      label: 'Health Insights',
      icon: <HealthAndSafetyIcon />,
      description: 'Analyze and clean up bugs, test cases, and product knowledge',
    },
    {
      id: 'automation',
      label: 'Automation',
      icon: <AutomationIcon />,
      description: 'Analyze manual test cases for automation readiness',
    },
  ];

  // Disabled navigation items (moved to bottom)
  const disabledNavigationItems = [
    {
      id: 'ui-explorer',
      label: 'UI Explorer',
      icon: <ExploreIcon />,
      description: 'Explore and analyze UI elements',
      disabled: true,
    },
  ];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <img
            src="/logo-stratovai-black.png"
            alt="Stratovai Logo"
            style={{
              height: '2.5rem',
              width: 'auto',
              marginRight: '12px',
              filter: isDarkMode ? 'drop-shadow(0 1px 2px rgba(255,255,255,0.1))' : 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
            }}
          />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Stratovai
            </Typography>
            <Typography variant="caption" color="text.secondary">
              QA Automation Platform
            </Typography>
          </Box>
        </Box>

        <SidebarProjectSwitcher
          selectedProject={selectedProject}
          projects={projects}
          projectsLoading={projectsLoading}
          projectsError={projectsError}
          onProjectSelect={onProjectSwitch}
        />
      </Box>

      {/* Active Navigation Items */}
      <List sx={{ flex: 1, pt: 1, display: 'flex', flexDirection: 'column' }}>
        {activeNavigationItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              data-testid={SV.nav.item(item.id)}
              selected={activeTab === item.id}
              onClick={() => onTabChange(item.id)}
              sx={{
                mx: 1,
                borderRadius: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: activeTab === item.id ? 'primary.contrastText' : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                secondary={item.description}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: activeTab === item.id ? 600 : 400,
                }}
                secondaryTypographyProps={{
                  fontSize: '0.75rem',
                  color: activeTab === item.id ? 'primary.contrastText' : 'text.secondary',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
        
        {/* Disabled Navigation Items (at bottom) */}
        <Box sx={{ mt: 'auto', pt: 2 }}>
          {disabledNavigationItems.map((item) => (
            <ListItem key={item.id} disablePadding>
              <Tooltip title="Coming Soon" placement="right" arrow>
                <span>
                  <ListItemButton
                    disabled
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      mb: 0.5,
                      opacity: 0.5,
                      cursor: 'not-allowed',
                      '&.Mui-disabled': {
                        cursor: 'not-allowed',
                      },
                    }}
                  >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    secondary={item.description}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: 400,
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                    }}
                  />
                  </ListItemButton>
                </span>
              </Tooltip>
            </ListItem>
          ))}
        </Box>
      </List>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Tooltip title={authenticatedUser?.name || authenticatedUser?.email || 'User'} placement="top" arrow>
            <span>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  mr: 1,
                  bgcolor: 'secondary.main',
                  cursor: 'pointer',
                }}
              >
                <UserIcon />
              </Avatar>
            </span>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {authenticatedUser?.name || authenticatedUser?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {authenticatedUser?.role || 'User'}
            </Typography>
          </Box>
        </Box>
        <ThemeToggle />
      </Box>
    </Box>
  );

  return (
    <>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {[...activeNavigationItems, ...disabledNavigationItems].find(item => item.id === activeTab)?.label || 'Dashboard'}
          </Typography>

          {/* Notifications */}
          <IconButton color="inherit" sx={{ mr: 1 }}>
            <Badge badgeContent={4} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* User Menu */}
          <IconButton
            onClick={handleUserMenuOpen}
            sx={{ ml: 1 }}
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              <UserIcon />
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
          },
        }}
      >
        <MenuItem onClick={handleProfileOpen}>
          <ListItemIcon>
            <UserIcon fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        {/* Show Settings only for admins */}
        {(authenticatedUser?.role === 'super_admin' || authenticatedUser?.role === 'project_admin') && (
          <MenuItem onClick={() => {
            onNavigateToSettings();
            handleUserMenuClose();
          }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => {
          onLogout();
          handleUserMenuClose();
        }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <ProfileDialog
        open={profileOpen}
        onClose={handleProfileClose}
        authenticatedUser={authenticatedUser}
        onChangePassword={() => {
          handleProfileClose();
          if (onOpenChangePassword) {
            onOpenChangePassword();
          }
        }}
      />

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content is rendered by parent (Dashboard). No offset element here to avoid extra whitespace. */
}

    </>
  );
};

export default ModernNavigation;